import { type ComponentType, createElement } from "react";

const failedModules = new Set<() => void>();

export function resetRendererModuleFailures() {
	for (const reset of failedModules) reset();
}

/** Module state only: no content or request data is retained between renders. */
export function createRendererModule<Props extends object>(
	importer: () => Promise<{ default: ComponentType<Props> }>,
) {
	let component: ComponentType<Props> | undefined;
	let pending: Promise<void> | undefined;
	let failure: unknown;
	let failed = false;
	function resetFailure() {
		failed = false;
		failure = undefined;
		failedModules.delete(resetFailure);
	}

	function preload(): Promise<void> {
		if (component) return Promise.resolve();
		if (pending) return pending;
		resetFailure();
		pending = importer().then(
			(module) => {
				component = module.default;
				pending = undefined;
			},
			(error: unknown) => {
				failure = error;
				failed = true;
				failedModules.add(resetFailure);
				pending = undefined;
				throw error;
			},
		);
		return pending;
	}

	function Renderer(props: Props) {
		if (component) return createElement(component, props);
		if (failed) throw failure;
		throw preload();
	}

	function prepare() {
		if (component) return undefined;
		if (failed) throw failure;
		return preload();
	}
	return { Renderer, preload, prepare };
}

export type RendererModule = {
	preload: () => Promise<void>;
	prepare: () => Promise<void> | undefined;
};

/** Includes nested areas, block lists and embedded rich-text blocks. */
export function discoverRendererTypes(value: unknown): Set<string> {
	const types = new Set<string>();
	const visited = new WeakSet<object>();
	function visit(node: unknown) {
		if (!node || typeof node !== "object" || visited.has(node)) return;
		// Expanded document links are destinations, not part of this page's UI.
		if (node !== value && "route" in node && "contentType" in node) return;
		visited.add(node);
		if ("contentType" in node && typeof node.contentType === "string") {
			types.add(node.contentType);
		}
		for (const child of Object.values(node)) visit(child);
	}
	visit(value);
	return types;
}

export async function preloadDiscoveredRenderers(
	value: unknown,
	registry: Readonly<Record<string, RendererModule>>,
) {
	await Promise.all(
		[...discoverRendererTypes(value)].map((type) => registry[type]?.preload()),
	);
}

/** Starts every required module before suspending, including nested blocks. */
export function prepareDiscoveredRenderers(
	value: unknown,
	registry: Readonly<Record<string, RendererModule>>,
) {
	const pending = [...discoverRendererTypes(value)]
		.map((type) => registry[type]?.prepare())
		.filter((promise) => promise !== undefined);
	if (pending.length) throw Promise.all(pending);
}
